import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi, saveAdminToken, clearAdminToken, getAdminToken } from "@/lib/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = not authed, obj = authed
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    if (!getAdminToken()) {
      // Try cookie-only auth once
      try {
        const { data } = await adminApi.get("/admin/auth/me");
        setUser(data);
      } catch (_e) {
        setUser(false);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const { data } = await adminApi.get("/admin/auth/me");
      setUser(data);
    } catch (_e) {
      clearAdminToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await adminApi.post("/admin/auth/login", { email, password });
    saveAdminToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await adminApi.post("/admin/auth/logout"); } catch (_e) { /* ignore */ }
    clearAdminToken();
    setUser(false);
  };

  const changePassword = async (current_password, new_password) => {
    await adminApi.post("/admin/auth/change-password", { current_password, new_password });
    // Refresh user
    const { data } = await adminApi.get("/admin/auth/me");
    setUser(data);
  };

  return (
    <AdminAuthContext.Provider value={{ user, loading, login, logout, changePassword, refresh: checkSession }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
